/*
 * Navbar – Sticky top navigation
 * Forest green on scroll, transparent at top
 * RTL Hebrew links with smooth scroll
 * Logo with botanical styling
 */

import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "wouter";

const LOGO = "https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/WhatsAppImage2026-03-22at11.32.24_87e6b455.jpeg";

const navLinks = [
  { href: "#hero", label: "ראשי" },
  { href: "#about", label: "אודות" },
  { href: "#specializations", label: "התמחויות" },
  { href: "#services", label: "אימונים" },
  { href: "#gallery", label: "גלריה" },
  { href: "#contact", label: "צרי קשר" },
];

const healthDeclarationLink = { href: "/health-declaration", label: "הצהרת בריאות" };

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [, navigate] = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    if (href.startsWith("/")) {
      navigate(href);
    } else {
      const el = document.querySelector(href);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <nav
      className={`fixed top-0 right-0 left-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#2D5A3D]/95 backdrop-blur-md shadow-lg"
          : "bg-transparent"
      }`}
    >
      <div className="container flex items-center justify-between h-16 md:h-20">
        {/* Logo with text */}
        <button
          onClick={() => handleNavClick("#hero")}
          className="flex items-center gap-2.5 group"
        >
          <div className="relative w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden shadow-md group-hover:shadow-lg transition-shadow duration-300">
            <img
              src={LOGO}
              alt="שרון גולדברג לוגו"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#E8A87C]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>
          <span
            className={`font-heading text-lg md:text-xl font-bold transition-colors duration-500 hidden sm:block ${
              scrolled ? "text-[#FFF8F0]" : "text-[#2D5A3D]"
            }`}
            style={{ fontFamily: "var(--font-heading)" }}
          >
            שרון גולדברג
          </span>
        </button>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNavClick(link.href)}
              className={`text-sm font-medium transition-colors duration-300 hover:opacity-80 ${
                scrolled ? "text-[#F5F0E8]" : "text-[#2D5A3D]"
              }`}
              style={{ fontFamily: "var(--font-body)" }}
            >
              {link.label}
            </button>
          ))}
          <button
            onClick={() => handleNavClick(healthDeclarationLink.href)}
            className={`text-sm font-medium transition-colors duration-300 hover:opacity-80 ${
              scrolled ? "text-[#F5F0E8]" : "text-[#2D5A3D]"
            }`}
            style={{ fontFamily: "var(--font-body)" }}
          >
            {healthDeclarationLink.label}
          </button>
          <button
            onClick={() => handleNavClick("#contact")}
            className="bg-[#E8A87C] hover:bg-[#d4956b] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all duration-300 hover:shadow-lg"
            style={{ fontFamily: "var(--font-body)" }}
          >
            ליצירת קשר
          </button>
        </div>

        {/* Mobile menu button */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`md:hidden p-2 transition-colors ${
            scrolled ? "text-[#FFF8F0]" : "text-[#2D5A3D]"
          }`}
          aria-label="תפריט"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#2D5A3D]/95 backdrop-blur-md overflow-hidden"
          >
            <div className="container py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className="text-[#F5F0E8] text-lg font-medium py-2 text-right hover:text-[#E8A87C] transition-colors"
                  style={{ fontFamily: "var(--font-body)" }}
                >
                  {link.label}
                </button>
              ))}
              <button
                onClick={() => handleNavClick(healthDeclarationLink.href)}
                className="text-[#F5F0E8] text-lg font-medium py-2 text-right hover:text-[#E8A87C] transition-colors"
                style={{ fontFamily: "var(--font-body)" }}
              >
                {healthDeclarationLink.label}
              </button>
              <button
                onClick={() => handleNavClick("#contact")}
                className="bg-[#E8A87C] hover:bg-[#d4956b] text-white font-semibold px-6 py-3 rounded-full mt-2 transition-all"
                style={{ fontFamily: "var(--font-body)" }}
              >
                ליצירת קשר
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
